import { useEffect, useState } from 'react'
import companyLogo from '../../../会社ロゴ.png'
import heroVisual from '../../assets/hero-visual-v1.png'
import aboutVisual from '../../assets/about-visual-v1.png'
import { usePageSeo } from '../../lib/seo'
import { getPublishedNewsItems, type NewsItem } from '../../lib/siteContent'

const navigationItems = [
  { href: '#about', label: '私たちについて' },
  { href: '#services', label: 'サービス' },
  { href: '#strengths', label: '強み' },
  { href: '#company', label: '会社情報' },
]

const serviceCards = [
  {
    icon: 'ri-lightbulb-flash-line',
    accent: 'from-rose-500 to-orange-400',
    title: '商品企画',
    description:
      '市場感や販売導線を踏まえながら、ブランドらしさが伝わる商品企画を一緒に組み立てます。',
    points: ['企画立案', 'ターゲット整理', '販売導線の設計'],
    examples: ['新商品の方向性を整理したい', '販促に合う企画を組み立てたい'],
  },
  {
    icon: 'ri-stack-line',
    accent: 'from-sky-500 to-cyan-400',
    title: 'OEM製造',
    description:
      '小物グッズを中心に、品質と見栄えの両立を意識したOEM製造をご提案します。',
    points: ['アクリルキーホルダー', '缶バッジ', 'アクリルボード', 'オーロラアート'],
    examples: ['仕様を相談しながら形にしたい', '見栄えと品質を両立したい'],
  },
  {
    icon: 'ri-store-2-line',
    accent: 'from-violet-500 to-fuchsia-500',
    title: 'ECサイト運営',
    description:
      '商品ページ設計、販促、運用改善まで見据え、売れる流れを継続的に支援します。',
    points: ['ショップ構築', '販促設計', '運用改善'],
    examples: ['販売導線を見直したい', '商品ページの見せ方を改善したい'],
  },
]

const strengths = [
  {
    title: '企画から販売支援まで一気通貫',
    description: '商品アイデアの整理から製造、販売面の設計まで、分断しない進行でスピード感を持って進めます。',
  },
  {
    title: 'ブランドの見せ方に強い',
    description: 'ただ作るだけではなく、ECやWeb上で価値が伝わる見せ方まで含めてご提案します。',
  },
  {
    title: '相談しやすいパートナー体制',
    description: '初回相談の段階から要件を整理し、まだ曖昧な構想でも前に進められる体制を整えています。',
  },
  {
    title: '東京・銀座拠点の対応力',
    description: '東京都中央区銀座を拠点に、企業様のものづくりとデジタル展開を柔軟にサポートします。',
  },
]

const workflow = [
  {
    step: '01',
    title: 'ヒアリング',
    description: '目的、ターゲット、ご希望納期を整理し、進め方の土台を固めます。',
  },
  {
    step: '02',
    title: '企画・提案',
    description: '商品仕様や販売イメージを踏まえ、実行しやすい提案に落とし込みます。',
  },
  {
    step: '03',
    title: '製造・構築',
    description: '品質とスケジュールの両立を意識しながら、必要な制作・構築を進行します。',
  },
  {
    step: '04',
    title: '運用・改善',
    description: '公開や販売開始後も、反応を見ながら改善ポイントを一緒に見つけていきます。',
  },
]

export default function Home() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [newsItems, setNewsItems] = useState<NewsItem[]>([])

  usePageSeo({
    title: '合同会社サクラネクサスメディア | 商品企画・OEM製造・EC運営支援',
    description:
      '合同会社サクラネクサスメディアは東京都中央区銀座を拠点に、商品企画、OEM製造、ECサイト運営、デジタルマーケティングを通じて企業様の成長を支援します。',
    path: '/',
  })

  useEffect(() => {
    void getPublishedNewsItems().then(setNewsItems)
  }, [])

  return (
    <div className="bg-[#fcfeff] text-slate-700">
      <header className="sticky top-0 z-50 border-b border-sky-100 bg-white/88 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <a href="#top" className="flex flex-col">
            <div>
              <p className="text-xs uppercase tracking-[0.35em] text-slate-400">Corporate Site</p>
              <p className="text-sm font-semibold text-slate-800">合同会社サクラネクサスメディア</p>
            </div>
          </a>

          <nav className="hidden items-center gap-7 lg:flex">
            {navigationItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="text-sm font-medium text-slate-500 transition hover:text-sky-700"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <a href="/contact#contact-form" className="brand-button">
              お問い合わせ
            </a>
          </div>

          <button
            type="button"
            onClick={() => setIsMenuOpen((open) => !open)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-sky-100 bg-white/90 text-sky-700 lg:hidden"
            aria-label="メニューを開く"
          >
            <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-xl`}></i>
          </button>
        </div>

        {isMenuOpen && (
          <div className="border-t border-sky-100 bg-white lg:hidden">
            <nav className="mx-auto flex max-w-7xl flex-col px-4 py-4 sm:px-6">
              {navigationItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setIsMenuOpen(false)}
                  className="border-b border-sky-100 py-3 text-sm font-medium text-slate-600 last:border-b-0"
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </div>
        )}
      </header>

      <main id="top">
        <section className="hero-shell overflow-hidden">
          <div className="mx-auto grid min-h-[calc(100vh-81px)] max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-[0.95fr_1.05fr] lg:px-8 lg:py-20">
            <div className="relative z-10">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-sky-100 bg-white/90 px-4 py-2 text-sm text-slate-500">
                <span className="h-2 w-2 rounded-full bg-rose-400"></span>
                Product Planning / OEM / EC Support
              </div>
              <h1 className="max-w-4xl text-4xl font-semibold leading-tight text-slate-800 sm:text-5xl lg:text-[3.6rem]">
                企画から販売支援まで、ブランド価値が伝わる
                <span className="text-[var(--color-accent-strong)]">
                  事業づくり
                </span>
                を。
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-8 text-slate-500 sm:text-lg">
                合同会社サクラネクサスメディアは、デジタルマーケティング、OEM製造、ECサイト運営を軸に、
                商品とブランドの魅力がしっかり届く仕組みづくりを支援します。
              </p>
              <div className="mt-9 flex flex-col gap-4 sm:flex-row">
                <a href="#services" className="brand-button">
                  サービスを見る
                </a>
                <a href="#company" className="subtle-button">
                  会社情報を見る
                </a>
              </div>
            </div>

            <div className="relative z-10">
              <div className="hero-panel">
                <div className="overflow-hidden rounded-[1.6rem] border border-white/10">
                  <img
                    src={heroVisual}
                    alt="商品企画、OEM製造、EC運営支援のイメージ"
                    className="h-[360px] w-full object-cover"
                  />
                </div>

                <div className="mt-6 grid gap-4 sm:grid-cols-3">
                  {serviceCards.map((service) => (
                    <div key={service.title} className="simple-tile">
                      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">Core</p>
                      <h3 className="mt-3 text-base font-semibold text-slate-800">{service.title}</h3>
                      <p className="mt-2 text-sm leading-7 text-slate-500">{service.points[0]}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="about" className="section-light">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 py-20 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8 lg:py-24">
            <div>
              <p className="section-eyebrow">About Us</p>
              <h2 className="section-title">ものづくりと販売支援を、同じ視点で考える会社です。</h2>
            </div>
            <div className="space-y-6 text-base leading-8 text-slate-500">
              <p>
                商品企画、OEM製造、ECサイト運営は、本来それぞれ切り離されるものではありません。サクラネクサスメディアは、
                「何を作るか」と「どう届けるか」を一体で考えることで、企業様のビジネス成長を後押しします。
              </p>
              <p>
                新商品の立ち上げ、既存商品の販促見直し、オンライン販売の強化など、構想段階からでもご相談いただけます。
                企画の解像度を上げながら、実行しやすい形へ整えていきます。
              </p>
              <p>
                合同会社サクラネクサスメディアは、サクラネクサスメディアやサクラネクサスの名称で検索される方にも分かりやすいよう、
                会社情報と事業内容を整理した企業サイトを公開しています。
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="soft-card">
                  <p className="text-sm font-semibold text-slate-700">対応テーマ</p>
                  <p className="mt-2 text-sm leading-7 text-slate-500">ブランド設計 / 商品企画 / OEM / EC運営 / デジタル導線の見直し</p>
                </div>
                <div className="soft-card">
                  <p className="text-sm font-semibold text-slate-700">ご相談例</p>
                  <p className="mt-2 text-sm leading-7 text-slate-500">新商品を形にしたい / 販売導線を整えたい / オンラインショップを強化したい</p>
                </div>
              </div>
              <div className="overflow-hidden rounded-[2rem] border border-sky-100 bg-white shadow-[0_20px_60px_rgba(125,170,230,0.10)]">
                <img
                  src={aboutVisual}
                  alt="商品企画やOEM相談のイメージ"
                  className="h-[320px] w-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section id="services" className="section-muted">
          <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
            <div className="mx-auto max-w-3xl text-center">
              <p className="section-eyebrow">Services</p>
              <h2 className="section-title">企業サイトとして伝わりやすい、3つの主力サービス</h2>
              <p className="mt-5 text-base leading-8 text-slate-500">
                初めて見た方にも事業内容がすぐ伝わるよう、提供価値と対応範囲が見える構成に整理しています。
              </p>
            </div>

            <div className="mt-14 grid gap-6 lg:grid-cols-3">
              {serviceCards.map((service) => (
                <article key={service.title} className="service-card">
                  <div className={`flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${service.accent} text-white shadow-lg shadow-rose-100/80`}>
                    <i className={`${service.icon} text-2xl`}></i>
                  </div>
                  <h3 className="mt-6 text-2xl font-semibold text-slate-800">{service.title}</h3>
                  <p className="mt-4 text-sm leading-7 text-slate-500">{service.description}</p>
                  <ul className="mt-6 space-y-3 text-sm text-slate-500">
                    {service.points.map((point) => (
                      <li key={point} className="flex items-center gap-3">
                        <span className="h-2 w-2 rounded-full bg-rose-400"></span>
                        {point}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8 rounded-[1.4rem] border border-sky-100 bg-[#f7fbff] p-5">
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">ご相談例</p>
                    <ul className="mt-3 space-y-2 text-sm leading-7 text-slate-500">
                      {service.examples.map((example) => (
                        <li key={example}>{example}</li>
                      ))}
                    </ul>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="strengths" className="section-light">
          <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-3xl">
                <p className="section-eyebrow">Strengths</p>
                <h2 className="section-title">一般的な企業HPとして必要な信頼感を、言葉と構成で補強。</h2>
              </div>
              <p className="max-w-xl text-base leading-8 text-slate-500">
                事業内容の理解、相談しやすさ、会社の実在感が伝わるように、トップページ全体の情報設計を見直しています。
              </p>
            </div>

            <div className="mt-14 grid gap-6 md:grid-cols-2">
              {strengths.map((item, index) => (
                <div key={item.title} className="soft-card">
                  <div className="mb-5 flex items-center justify-between">
                    <span className="text-sm font-semibold uppercase tracking-[0.24em] text-rose-500">Point {index + 1}</span>
                    <i className="ri-arrow-right-up-line text-xl text-slate-300"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-slate-800">{item.title}</h3>
                  <p className="mt-4 text-sm leading-7 text-slate-500">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section-light border-y border-sky-100 bg-white">
          <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
            <div className="mx-auto max-w-3xl text-center">
              <p className="section-eyebrow">Flow</p>
              <h2 className="section-title">ご相談から実行までの流れ</h2>
            </div>

            <div className="mt-14 grid gap-6 lg:grid-cols-4">
              {workflow.map((item) => (
                <div key={item.step} className="rounded-[2rem] border border-sky-100 bg-[#f7fbff] p-7">
                  <p className="text-sm font-semibold uppercase tracking-[0.24em] text-rose-500">{item.step}</p>
                  <h3 className="mt-4 text-xl font-semibold text-slate-800">{item.title}</h3>
                  <p className="mt-4 text-sm leading-7 text-slate-500">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section-light bg-[linear-gradient(180deg,#ffffff_0%,#f6fbff_100%)]">
          <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
            <div className="mx-auto max-w-3xl text-center">
              <p className="section-eyebrow">News</p>
              <h2 className="section-title">お知らせ</h2>
            </div>

            <div className="mx-auto mt-12 max-w-4xl space-y-4">
              {newsItems.map((item) => (
                <a
                  key={item.id}
                  href={`/news/${item.slug}`}
                  className="block rounded-[1.75rem] border border-sky-100 bg-white px-6 py-5 shadow-[0_20px_50px_rgba(125,170,230,0.10)] transition hover:border-sky-200 hover:shadow-[0_24px_55px_rgba(125,170,230,0.14)]"
                >
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                      <span className="text-sm font-semibold text-slate-400">{item.publishedAt.replaceAll('-', '.')}</span>
                      <span className="inline-flex w-fit rounded-full bg-rose-100 px-3 py-1 text-xs font-semibold text-rose-600">
                        {item.category}
                      </span>
                      <span className="text-base font-medium text-slate-700">{item.title}</span>
                    </div>
                    <span className="text-sm font-medium text-slate-400">詳細を見る</span>
                  </div>
                </a>
              ))}
              {newsItems.length === 0 && (
                <div className="rounded-[1.75rem] border border-sky-100 bg-white px-6 py-8 text-center text-sm text-slate-400 shadow-[0_20px_50px_rgba(125,170,230,0.08)]">
                  現在ご案内できるお知らせはありません。
                </div>
              )}
            </div>
          </div>
        </section>

        <section id="company" className="section-muted">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 py-20 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8 lg:py-24">
            <div>
              <p className="section-eyebrow">Company</p>
              <h2 className="section-title">会社情報</h2>
              <p className="mt-6 text-base leading-8 text-slate-500">
                企業サイトとして必要な基本情報を見やすく整理し、安心してお問い合わせいただける構成にしています。
              </p>
              <div className="mt-8 rounded-[2rem] border border-sky-100 bg-white p-6">
                <p className="text-sm uppercase tracking-[0.24em] text-slate-400">Access</p>
                <p className="mt-3 text-lg font-semibold text-slate-800">東京都中央区銀座1丁目12番4号 N&E BLD.6F</p>
                <p className="mt-2 text-sm text-slate-500">東京・銀座エリアを拠点に、商品企画やオンライン展開のご相談に対応しています。</p>
              </div>
            </div>

            <div className="rounded-[2rem] border border-sky-100 bg-white p-4 shadow-[0_20px_60px_rgba(125,170,230,0.12)]">
              <div className="overflow-hidden rounded-[1.6rem] border border-sky-100">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3241.0977!2d139.7648!3d35.6762!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x60188be701836fbb%3A0x3f5b3f8b8b8b8b8b!2z44CSMTA0LTAwNjEg5p2x5Lqs6YO95Lit5aSu5Yy66YqA5bqn77yR5LiB55uu77yR77yS4oiS77yU!5LitJkUgQkxELjZG!5e0!3m2!1sja!2sjp!4v1733472000000!5m2!1sja!2sjp"
                  width="100%"
                  height="340"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="合同会社サクラネクサスメディアの所在地"
                ></iframe>
              </div>
              <div className="grid gap-4 p-6 sm:grid-cols-2">
                <div className="company-item">
                  <span className="company-label">会社名</span>
                  <span className="company-value">合同会社サクラネクサスメディア</span>
                </div>
                <div className="company-item">
                  <span className="company-label">設立</span>
                  <span className="company-value">2025年10月1日</span>
                </div>
                <div className="company-item">
                  <span className="company-label">所在地</span>
                  <span className="company-value">〒104-0061 東京都中央区銀座1丁目12番4号 N&E BLD.6F</span>
                </div>
                <div className="company-item">
                  <span className="company-label">お問い合わせ</span>
                  <span className="company-value">各種ご相談はお問い合わせフォームより承っております。</span>
                </div>
                <div className="company-item">
                  <span className="company-label">初回相談</span>
                  <span className="company-value">内容を確認のうえ、順次ご返信いたします。</span>
                </div>
                <div className="company-item sm:col-span-2">
                  <span className="company-label">事業内容</span>
                  <span className="company-value">デジタルマーケティング / OEM製造 / ECサイト運営</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section-light bg-[linear-gradient(180deg,#f8fbff_0%,#ffffff_100%)]">
          <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
            <div className="rounded-[2rem] border border-sky-100 bg-white p-8 text-center shadow-[0_20px_60px_rgba(125,170,230,0.12)] lg:p-12">
              <p className="section-eyebrow">Contact</p>
              <h2 className="mt-4 text-3xl font-semibold leading-tight text-slate-800">お問い合わせはこちら</h2>
              <p className="mx-auto mt-5 max-w-3xl text-sm leading-8 text-slate-500">
                ご相談内容を整理しやすいよう、フォームは別ページに集約しています。商品企画、OEM製造、EC運営のご相談は、
                専用フォームからお気軽にお送りください。
              </p>
              <div className="mt-8 flex items-center justify-center">
                <a href="/contact#contact-form" className="brand-button">
                  お問い合わせ
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-sky-100 bg-white">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-12 sm:px-6 lg:grid-cols-[1.15fr_0.85fr_0.8fr] lg:px-8">
          <div>
            <img src={companyLogo} alt="合同会社サクラネクサスメディア" className="h-12 w-auto" />
            <p className="mt-5 max-w-md text-sm leading-7 text-slate-500">
              合同会社サクラネクサスメディアは、デジタルマーケティング、OEM製造、ECサイト運営を通じて企業様の成長を支援します。
            </p>
          </div>
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-[0.24em] text-slate-400">Navigation</h3>
            <div className="mt-5 flex flex-col gap-3 text-sm text-slate-600">
              {navigationItems.map((item) => (
                <a key={item.href} href={item.href} className="transition hover:text-sky-700">
                  {item.label}
                </a>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-[0.24em] text-slate-400">Contact</h3>
            <div className="mt-5 space-y-3 text-sm leading-7 text-slate-600">
              <p>お問い合わせはメールフォームよりお願いいたします。</p>
              <p>〒104-0061 東京都中央区銀座1丁目12番4号 N&E BLD.6F</p>
            </div>
          </div>
        </div>
        <div className="border-t border-sky-100 px-4 py-6 text-center text-xs text-slate-400 sm:px-6 lg:px-8">
          © 2026 Sakuranexus Media LLC. All rights reserved.
        </div>
      </footer>
    </div>
  )
}
